import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Button from "../ui/Button";
import '../ui/Form.scss';
import { setOrderDetails } from "../../store/checkoutSlice.jsx";
import { useDispatch, useSelector } from "react-redux";
import { calculateRawTotal } from "../../utils/priceHandler.js";
import { getImageFromDB } from "../../utils/indexedDB.js";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { useAppNavigate } from "../../hooks/useAppNavigate.js";
import { scrollToTop } from "../../utils/scrollToTop.js";
import { trackCustomEvent } from "../../utils/eventsTracking/trackCustomEvent.js";
import { checkoutFinalize } from "../../utils/checkoutFinalize.js";
import { bundleAwareCartItems } from "../../utils/bundleAwareCartItems.js";

const PaymentStep = ({
  comment,
  affiliate,
  isMobile,
  isSubmittingCheckout,
  setIsSubmittingCheckout,
  setCheckoutError,
  formikRef,
  showOrderSummaryFunction,
  isUpdatingPaymentIntent,
  clientSecret,
  isTablet,
  updateStripeIntent,
  isFormCompleted,
}) => {
  const navigate = useAppNavigate();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const promoCodes = useSelector((state) => state.cart.submittedPromoCodes);
  const cartItems = useSelector((state) => state.cart.cartItems);
  const transformedCartItems = bundleAwareCartItems(cartItems);


  const handleConfirmPayment = async () => {
    if (!stripe || !elements) {
      setCheckoutError(["Stripe is not loaded."]);
      return;
    }

    setCheckoutError([]);
    setIsSubmittingCheckout(true);
    try {
      const customerId = await updateStripeIntent();
      const values = formikRef?.current?.values;
      const confirmParams = {
        return_url: `${window.location.origin}/checkout/success`,
        payment_method_data: {
          billing_details: {
            name: `${values.firstName} ${values.surname}`,
            email: values.email,
            phone: values.phone,
            address: {
              line1: values.address,
              line2: values.address2 || '',
              city: values.city,
              state: values.state,
              postal_code: values.postalCode,
              country: 'US',
            },
          },
        },
      };

      let result;
      const secretType = clientSecret.startsWith('seti_') ? 'setup' : 'payment';

      if (secretType === 'setup') {
        result = await stripe.confirmSetup({
          elements,
          confirmParams,
          redirect: "if_required",
        });
      } else {
        result = await stripe.confirmPayment({
          elements,
          confirmParams,
          redirect: "if_required",
        });
      }

      const { error, setupIntent, paymentIntent } = result;

      if (error) {
        setCheckoutError([error.message]);
        return;
      }

      let intent = paymentIntent || setupIntent;
      const paymentMethodId = intent.payment_method;

      if (intent.status !== "succeeded") {
        setCheckoutError(["Payment failed. Please try again."]);
        return;
      }

      await checkoutFinalize({
        cartItems,
        transformedCartItems,
        setCheckoutError,
        values,
        comment,
        affiliate,
        latestPromoCodes: promoCodes,
        getImageFromDB,
        dispatch,
        setOrderDetails,
        navigate,
        scrollToTop,
        paymentMethodId,
        customerId,
        intentId: intent.id,
        intentType: secretType
      });

    } catch (error) {
      setCheckoutError([error.message || 'An unexpected error occurred. Please try again later.']);
      trackCustomEvent('purchase_failed', {
        total: calculateRawTotal(cartItems, promoCodes),
        products: cartItems.map(item => ({
          sku: item.sku,
          price: item.price,
          title: item.name,
          quantity: item.quantity,
          flavor: item.tag || null,
          subscription: item.subscription || false,
        })),
        error: error.message,
      }, affiliate?.code || null);
    } finally {
      setIsSubmittingCheckout(false);
    }
  };

  return (
    <div className="fade-step flex flex-col gap-5 w-full md:w-full  mb-5">
      {showOrderSummaryFunction && showOrderSummaryFunction()}
      {!isTablet && <h5 className="form-title body-heading text-left color-swap px-[20px]">Payment</h5>}
      <div className="flex flex-col gap-3 mt-3 px-[20px]">
        <PaymentElement
          options={{
            layout: {
              type: 'accordion',
              defaultCollapsed: false,
            },
            paymentMethodOrder: ['card', 'apple_pay'],
            fields: {
              billingDetails: {
                name: 'never',
                email: 'never',
                phone: 'never',
                address: 'never'
              },
            },
          }}
        />
      </div>
      <div className="px-[20px]">
        <Button
          type="button"
          className={`${isMobile ? 'w-full min-w-full' : ''}`}
          variant="filled"
          onClick={handleConfirmPayment}
          disabled={isSubmittingCheckout || isUpdatingPaymentIntent || !isFormCompleted}
        >
          {isSubmittingCheckout ? 'Processing...' : 'Order Now'}
        </Button>
      </div>
    </div>
  );
};

PaymentStep.propTypes = {
  comment: PropTypes.string,
  affiliate: PropTypes.object,
  isMobile: PropTypes.bool,
  isSubmittingCheckout: PropTypes.bool.isRequired,
  setIsSubmittingCheckout: PropTypes.func.isRequired,
  setCheckoutError: PropTypes.func.isRequired,
  formikRef: PropTypes.object,
  showOrderSummaryFunction: PropTypes.func,
  isUpdatingPaymentIntent: PropTypes.bool,
  clientSecret: PropTypes.string,
  isTablet: PropTypes.bool,
  updateStripeIntent: PropTypes.func.isRequired,
  isFormCompleted: PropTypes.bool.isRequired
};

export default PaymentStep;